import { InteractivityPoll } from 'modules/AnchorAPI/v3/episodes/fetchInteractivityPoll';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AnchorAPI } from 'modules/AnchorAPI';

export const INTERACTIVE_POLL_KEY = 'INTERACTIVE_POLL_KEY';
export const INTERACTIVE_QUESTION_KEY = 'INTERACTIVE_QUESTION_KEY';

export function useFetchPoll(episodeId: string) {
  const { data, status } = useQuery([INTERACTIVE_POLL_KEY, episodeId], () =>
    AnchorAPI.fetchInteractivityPoll(episodeId)
  );
  return { data, status };
}

export function useDeletePoll() {
  const queryClient = useQueryClient();
  const { mutate, status } = useMutation(
    ({ episodeId, userId }: { episodeId: string; userId: number }) =>
      AnchorAPI.deleteInteractivityPoll(episodeId, userId),
    {
      onSuccess: (res, { episodeId }) => {
        return queryClient.invalidateQueries([INTERACTIVE_POLL_KEY, episodeId]);
      },
    }
  );
  return { deletePoll: mutate, status };
}

export function useUpdatePoll() {
  const queryClient = useQueryClient();
  const { mutate, status } = useMutation(
    ({
      episodeId,
      poll: updates,
    }: {
      episodeId: string;
      poll: Partial<InteractivityPoll>;
    }) => AnchorAPI.updateInteractivityPoll(episodeId, updates),
    {
      onSuccess: (res, { episodeId }) => {
        return queryClient.invalidateQueries([INTERACTIVE_POLL_KEY, episodeId]);
      },
    }
  );
  return { updatePoll: mutate, status };
}

export function useFetchQuestionAndResponses(episodeId: string) {
  const { data, status } = useQuery([INTERACTIVE_QUESTION_KEY, episodeId], () =>
    fetchListenerQuestionAndResponses(episodeId)
  );
  return { data, status };
}

async function fetchListenerQuestionAndResponses(episodeId: string) {
  const listenerQuestionResult = await AnchorAPI.fetchListenerQuestion(
    episodeId
  );
  if (!listenerQuestionResult) {
    return {};
  }
  const { questionId } = listenerQuestionResult;
  const listenerQuestionResponsesResult = await AnchorAPI.fetchListenerQuestionReplies(
    episodeId,
    questionId
  );
  return {
    question: listenerQuestionResult,
    featuredReplies: listenerQuestionResponsesResult.featuredResponses,
    allReplies: listenerQuestionResponsesResult.allResponses,
  };
}

export function useDeleteQuestion() {
  const queryClient = useQueryClient();
  const { mutate, status } = useMutation(
    ({ episodeId, questionId }: { episodeId: string; questionId: number }) =>
      AnchorAPI.deleteListenerQuestion(episodeId, questionId),
    {
      onSuccess: (res, { episodeId }) => {
        return queryClient.invalidateQueries([
          INTERACTIVE_QUESTION_KEY,
          episodeId,
        ]);
      },
    }
  );
  return { deleteQuestion: mutate, status };
}

export function useModifyQuestionReply() {
  const queryClient = useQueryClient();
  const { mutate, status } = useMutation(
    ({
      episodeId,
      questionId,
      replyId,
      spotifyUserId,
      action,
    }: {
      episodeId: string;
      questionId: number;
      replyId: string;
      spotifyUserId: string;
      action: 'feature' | 'unfeature';
    }) =>
      AnchorAPI.modifyListenerQuestionReply(
        episodeId,
        questionId,
        replyId,
        spotifyUserId,
        action
      ),
    {
      onSuccess: (res, { episodeId }) => {
        return queryClient.invalidateQueries([
          INTERACTIVE_QUESTION_KEY,
          episodeId,
        ]);
      },
    }
  );
  return { modifyQuestionReply: mutate, status };
}

/**
 * Currently used only for blocking users in context of
 * episode interactivity (Q&A), so passing the episodeId
 * re-fetches the question / coupled to Q&A
 */
export function useBlockSpotifyUserFromQuestion() {
  const queryClient = useQueryClient();
  const { mutate, status } = useMutation(
    ({
      episodeId,
      spotifyUserId,
    }: {
      episodeId: string;
      spotifyUserId: string;
    }) => AnchorAPI.blockSpotifyUser(spotifyUserId),
    {
      onSuccess: (res, { episodeId }) => {
        return queryClient.invalidateQueries([
          INTERACTIVE_QUESTION_KEY,
          episodeId,
        ]);
      },
    }
  );
  return { blockSpotifyUserFromQuestion: mutate, status };
}
